/**
 * useResourceTypes Hook
 * Manages ResourceType data and operations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchResourceTypes,
  fetchResourceType,
  createResourceType,
  updateResourceType,
  deleteResourceType,
} from '../services/resourceService';

/**
 * Custom hook for managing resource type data and operations
 * @param {Object} options - Hook options
 * @param {Object} options.filter - Optional filter criteria
 * @param {boolean} options.enabled - Whether to enable the query (default: true)
 * @returns {Object} Resource type data, mutations, loading states, and utility functions
 */
export function useResourceTypes(options = {}) {
  const { filter = null, enabled = true } = options;
  const queryClient = useQueryClient();

  // Fetch all resource types
  const {
    data: resourceTypes = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['resourceTypes', filter],
    queryFn: () => fetchResourceTypes(filter),
    enabled,
  });

  // Create resource type mutation
  const createMutation = useMutation({
    mutationFn: createResourceType,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['resourceTypes'] });
    },
  });

  // Update resource type mutation
  const updateMutation = useMutation({
    mutationFn: updateResourceType,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['resourceTypes'] });
    },
  });

  // Delete resource type mutation
  const deleteMutation = useMutation({
    mutationFn: deleteResourceType,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['resourceTypes'] });
    },
  });

  // Utility functions
  const getActiveResourceTypes = () => resourceTypes.filter((rt) => rt.status === 'ACTIVE');

  const getInactiveResourceTypes = () => resourceTypes.filter((rt) => rt.status === 'INACTIVE');

  const filterByDomain = (domain) =>
    resourceTypes.filter((rt) =>
      !rt.domainRestrictions ||
      rt.domainRestrictions.length === 0 ||
      rt.domainRestrictions.includes(domain)
    );

  const searchResourceTypes = (searchTerm) => {
    if (!searchTerm) return resourceTypes;
    const searchLower = searchTerm.toLowerCase();
    return resourceTypes.filter((rt) =>
      rt.name?.toLowerCase().includes(searchLower) ||
      rt.description?.toLowerCase().includes(searchLower)
    );
  };

  return {
    // Data
    resourceTypes,
    isLoading,
    error,

    // Mutations
    create: createMutation.mutate,
    createAsync: createMutation.mutateAsync,
    update: updateMutation.mutate,
    updateAsync: updateMutation.mutateAsync,
    remove: deleteMutation.mutate,
    removeAsync: deleteMutation.mutateAsync,

    // Mutation states
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
    createError: createMutation.error,
    updateError: updateMutation.error,
    deleteError: deleteMutation.error,

    // Utility functions
    refetch,
    getActiveResourceTypes,
    getInactiveResourceTypes,
    filterByDomain,
    searchResourceTypes,
  };
}

/**
 * Custom hook for fetching a single resource type
 * @param {string} id - Resource type ID
 * @param {Object} options - Hook options
 * @param {boolean} options.enabled - Whether to enable the query (default: true)
 * @returns {Object} Resource type data and loading state
 */
export function useResourceType(id, options = {}) {
  const { enabled = true } = options;

  const { data: resourceType, isLoading, error } = useQuery({
    queryKey: ['resourceType', id],
    queryFn: () => fetchResourceType(id),
    enabled: enabled && !!id,
  });

  return {
    resourceType,
    isLoading,
    error,
  };
}
